// ============================================================
//  src/pages/Inventory/QuotationEntryGST.jsx
//  FINAL VERSION — Merged modern UI + correct backend API wiring
// ============================================================

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ArrowLeft,
  Save,
  CheckCircle,
  Upload,
  RefreshCw,
  Info,
  X,
  FileText,
  ClipboardList,
} from "lucide-react";

import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";

// ─── Session helpers ─────────────────────────────────────────
const getSession = () => {
  let moduleSession = {};

  try {
    moduleSession = JSON.parse(
      localStorage.getItem("moduleSession") || "{}"
    );
  } catch (e) {
    console.error("Invalid moduleSession:", e);
  }

  return {
    branchId:
      moduleSession.branchId ||
      localStorage.getItem("branchId") ||
      "",

    branchType:
      moduleSession.branchType ||
      localStorage.getItem("branchType") ||
      "",

    loginUserId:
      moduleSession.user?.id ||
      localStorage.getItem("userId") ||
      localStorage.getItem("loginuserid") ||
      "",

    loginUserType:
      moduleSession.user?.userType ||
      localStorage.getItem("loginUserType") ||
      "",

    deptName:
      moduleSession.user?.deptName ||
      localStorage.getItem("deptName") ||
      "",

    finYearId:
      moduleSession.finYearId ||
      localStorage.getItem("finYearId") ||
      "",

    finYear:
      moduleSession.finYear ||
      localStorage.getItem("finYear") ||
      "",
  };
};

// ─── Axios instance ──────────────────────────────────────────
const API_BASE = "https://nissi-india.com/api/quotationEntryGST";
const api = axios.create({ baseURL: API_BASE });
api.interceptors.request.use((cfg) => {
  const s = getSession();
  if (s.finYearId) cfg.headers["x-finyearid"] = s.finYearId;
  if (s.loginUserType) cfg.headers["x-loginusertype"] = s.loginUserType;
  if (s.deptName) cfg.headers["x-deptname"] = s.deptName;
  if (s.branchId) cfg.headers["x-branchid"] = s.branchId;
  return cfg;
});

// ─── Pure helpers ────────────────────────────────────────────
const toNum = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};
const toInt = (v, d = 0) => Math.trunc(toNum(v, d));
const round2 = (n) => Math.round((toNum(n) + Number.EPSILON) * 100) / 100;
const fmt2 = (n) => round2(n).toFixed(2);
const today = () => new Date().toISOString().split("T")[0];

const isoToDDMMYYYY = (iso) => {
  if (!iso) return "";
  const p = String(iso).split("T")[0].split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso;
};
const ddmmyyyyToISO = (dmy) => {
  if (!dmy) return "";
  const p = String(dmy).split("/");
  return p.length === 3
    ? `${p[2]}-${String(p[1]).padStart(2, "0")}-${String(p[0]).padStart(2, "0")}`
    : dmy;
};

const blankLine = () => ({
  price: "0",
  discount: "0",
  sgst: "0",
  cgst: "0",
  igst: "0",
  pfStatus: 0,
  approveFlag: 0,
  approveNo: "",
  taxable: 0,
  total: 0,
});

const blankFooter = () => ({
  freight: "0",
  freightTaxOn: false,
  freightSgst: "0",
  freightCgst: "0",
  freightIgst: "0",
  insurance: "0",
  insuranceTaxOn: false,
  insuranceSgst: "0",
  insuranceCgst: "0",
  insuranceIgst: "0",
  pf: "0",
  pfTaxOn: false,
  pfSgst: "0",
  pfCgst: "0",
  pfIgst: "0",
  tcsPercent: "0",
  roundoff: "0",
  location: "",
  paymentTerms: "",
  delivery: "",
  validity: "",
  discountPercent: "0",
  salesTax: "0",
});

const recalcLine = (qty, line) => {
  const q = toNum(qty, 0);
  const p = toNum(line.price, 0);
  const dis = toNum(line.discount, 0);
  const sg = toNum(line.sgst, 0);
  const cg = toNum(line.cgst, 0);
  const ig = toNum(line.igst, 0);
  const amount = q * p;
  const discAmt = (amount * dis) / 100;
  const taxable = amount - discAmt;
  const taxAmt = (taxable * (sg + cg + ig)) / 100;
  return { ...line, taxable: round2(taxable), total: round2(taxable + taxAmt) };
};

function TaxSelect({ options, value, onChange, disabled, className = "", isDark }) {
  return (
    <select
      value={String(value ?? "0")}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className={`border rounded px-1 py-1 text-xs ${
        isDark
          ? "bg-slate-800 border-slate-600 text-white"
          : "bg-white border-slate-300"
      } ${className}`}
    >
      <option value="0">0</option>
      {(options || []).map((o) => (
        <option key={`${o.id}-${o.label}`} value={String(o.label ?? o.id ?? "")}>
          {o.label ?? o.id}
        </option>
      ))}
    </select>
  );
}

export default function QuotationEntryGST() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
    isDark
      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
  }`;

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark ? "text-slate-300" : "text-slate-700"
  }`;

  const numInputClass = `${inputClass} text-right`;

  const quotationIdParam = searchParams.get("quotationId") || "";
  const prParam = searchParams.get("pr") || "";
  const suParam = searchParams.get("su") || "";
  const itmParam = searchParams.get("itm") || "";
  const isEditMode = !!quotationIdParam;

  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const [quotationId, setQuotationId] = useState(toInt(quotationIdParam, 0));
  const [quotationNo, setQuotationNo] = useState("");
  const [quotationDate, setQuotationDate] = useState(today());
  const [enquiryId, setEnquiryId] = useState("Select");
  const [reqSiteId, setReqSiteId] = useState("Select");
  const [indentNo, setIndentNo] = useState("");
  const [indentDate, setIndentDate] = useState(today());
  const [requestedFor, setRequestedFor] = useState("");
  const [remarks, setRemarks] = useState("");
  const [projectName, setProjectName] = useState("");
  const [employeeId, setEmployeeId] = useState("Select");

  const [enquiryList, setEnquiryList] = useState([]);
  const [reqSiteList, setReqSiteList] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);

  const [tax1, setTax1] = useState([]);
  const [tax2, setTax2] = useState([]);
  const [tax3, setTax3] = useState([]);

  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [footers, setFooters] = useState({});

  const [approvedSupIdx, setApprovedSupIdx] = useState(null);

  const [docs, setDocs] = useState([]);
  const [docFile, setDocFile] = useState(null);
  const docInputRef = useRef(null);

  const [userRights, setUserRights] = useState({
    canApprove: false,
    canSubmit: true,
    isLocked: false,
    mdApproved: false,
    submitBlocked: false,
    canAdd: false,
  });

  const [showModal, setShowModal] = useState(false);
  const [modalItem, setModalItem] = useState(null);
  const [modalRows, setModalRows] = useState([]);

  const supplierTotals = useMemo(() => {
    return suppliers.map((_, si) => {
      const subTotal = items.reduce(
        (sum, it) => sum + toNum(it.lines?.[si]?.total, 0),
        0
      );
      const f = footers[si] || blankFooter();

      const freight = toNum(f.freight, 0);
      const insurance = toNum(f.insurance, 0);
      const pf = toNum(f.pf, 0);

      const frTax = f.freightTaxOn
        ? (freight * (toNum(f.freightSgst) + toNum(f.freightCgst) + toNum(f.freightIgst))) / 100
        : 0;
      const inTax = f.insuranceTaxOn
        ? (insurance * (toNum(f.insuranceSgst) + toNum(f.insuranceCgst) + toNum(f.insuranceIgst))) / 100
        : 0;
      const pfTax = f.pfTaxOn
        ? (pf * (toNum(f.pfSgst) + toNum(f.pfCgst) + toNum(f.pfIgst))) / 100
        : 0;

      const base = subTotal + freight + frTax + insurance + inTax + pf + pfTax;
      const tcsPct = toNum(f.tcsPercent, 0);
      const tcsAmt = (base * tcsPct) / 100;
      const roundoff = toNum(f.roundoff, 0);
      const grandTotal = Math.round(base + tcsAmt + roundoff);

      return {
        subTotal: round2(subTotal),
        frTax: round2(frTax),
        inTax: round2(inTax),
        pfTax: round2(pfTax),
        tcsAmt: round2(tcsAmt),
        grandTotal,
      };
    });
  }, [items, suppliers, footers]);

  const loadInit = useCallback(async () => {
    const s = getSession();
    if (!s.branchId) {
      toast.error("Branch ID missing from session — please login again");
      return;
    }
    try {
      setLoading(true);
      const res = await api.get("/init", {
        params: {
          branchId: s.branchId,
          loginUserType: s.loginUserType,
          deptName: s.deptName,
        },
      });
      const d = res.data;
      if (!d.success) throw new Error(d.message);

      if (!isEditMode) setQuotationNo(d.quotationNo || "");

      setEnquiryList(d.enquiryNos || []);
      setReqSiteList(d.reqSites || []);
      setEmployeeList(d.employees || []);

      const mapTax = (arr) =>
        (arr || []).map((r) => ({
          id: r.fld_taxpercentid ?? r.id ?? r.value,
          label: r.fld_taxpercentage ?? r.label ?? r.text,
        }));
      setTax1(mapTax(d.taxes?.tax1));
      setTax2(mapTax(d.taxes?.tax2));
      setTax3(mapTax(d.taxes?.tax3));

      setUserRights((prev) => ({
        ...prev,
        canApprove: d.userRights?.canApprove ?? false,
        canSubmit: d.userRights?.canSubmit ?? true,
      }));
    } catch (e) {
      toast.error(`Init failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [isEditMode]);

  const loadGrid = useCallback(async () => {
    if (!prParam || !suParam || !itmParam) return;
    try {
      setLoading(true);
      const res = await api.post("/grid", {
        prCsv: prParam,
        supplierCsv: suParam,
        itemCsv: itmParam,
      });
      const d = res.data;
      if (!d.success) throw new Error(d.message);

      if (d.header?.indentNo) setIndentNo(d.header.indentNo);
      if (d.header?.indentDate)
        setIndentDate(ddmmyyyyToISO(d.header.indentDate) || today());
      if (d.header?.projectName) setProjectName(d.header.projectName);

      const rows = d.rows || [];
      const supplierIds = suParam.split(",").map((x) => x.trim());
      const firstRow = rows[0] || {};

      const builtSuppliers = supplierIds.slice(0, 5).map((sid, si) => ({
        supplierId: toInt(sid, 0),
        supplierName: String(firstRow[`s${si + 1}address`] || `Supplier ${si + 1}`)
          .split(".")[0]
          .trim(),
        supplierAddress: String(firstRow[`s${si + 1}address`] || ""),
        make: "",
      }));
      setSuppliers(builtSuppliers);

      const builtItems = rows.map((r, ri) => {
        const qty = String(r.fld_quantity ?? r.qty ?? "0");
        const lines = builtSuppliers.map((_, si) => {
          const base = blankLine();
          base.price = String(r[`supplierprice${si + 1}`] ?? "0");
          base.discount = String(r[`supplierdis${si + 1}`] ?? "0");
          base.sgst = String(r[`suppliervat${si + 1}`] ?? "0");
          base.cgst = String(r[`suppliercst${si + 1}`] ?? "0");
          base.igst = String(r[`suppliered${si + 1}`] ?? "0");
          base.pfStatus = toInt(r[`pfstatus${si + 1}`], 0);
          return recalcLine(qty, base);
        });
        return {
          rowKey: `${r.fld_itemid}-${ri}`,
          itemId: toInt(r.fld_itemid, 0),
          uomId: toInt(r.fld_uomid, 0),
          itemName: String(r.fld_itemname || ""),
          uomName: String(r.fld_uomcode || r.fld_uomname || ""),
          qty,
          lines,
        };
      });
      setItems(builtItems);

      const fb = {};
      builtSuppliers.forEach((_, si) => {
        fb[si] = blankFooter();
      });
      setFooters(fb);

      setUserRights((prev) => ({ ...prev, canAdd: false }));
    } catch (e) {
      toast.error(`Load grid failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [prParam, suParam, itmParam]);

const loadView = useCallback(async () => {
  if (!quotationIdParam) return;

  const s = getSession();

  try {
    setLoading(true);

    const res = await api.get("/view", {
      params: {
        quotationId: quotationIdParam,
        loginUserType: s.loginUserType,
        deptName: s.deptName,
      },
    });

    const d = res.data;

    if (!d.success) {
      throw new Error(d.message || "Failed to load quotation");
    }

    // =========================================================
    // 1. MASTER DATA
    // =========================================================
    const mas = d.quotationMas || d.master || {};

    setQuotationId(toInt(mas.fld_quotationid, 0));
    setQuotationNo(String(mas.fld_quotationNo || ""));

    setQuotationDate(
      ddmmyyyyToISO(mas.fld_QuotationDate) || today()
    );

    setEnquiryId(
      toInt(mas.fld_Enquiryid, 0) > 0
        ? String(mas.fld_Enquiryid)
        : "Select"
    );

    setReqSiteId(
      toInt(mas.fld_req_site, 0) > 0
        ? String(mas.fld_req_site)
        : "Select"
    );

    setIndentNo(String(mas.fld_indentno || ""));

    setIndentDate(
      ddmmyyyyToISO(mas.fld_indentdate) || today()
    );

    setRequestedFor(String(mas.fld_req_for || ""));
    setRemarks(String(mas.fld_remarks || ""));
    setProjectName(String(mas.projectName || ""));

    setEmployeeId(
      String(mas.fld_employeeid || "").trim()
        ? String(mas.fld_employeeid)
        : "Select"
    );

    setDocs(d.docs || []);

    setUserRights((prev) => ({
      ...prev,
      ...(d.userRights || {}),
      submitBlocked: !!d.submitBlocked,
    }));

    // =========================================================
    // 2. BACKEND RETURNS FLAT ROWS
    //    Convert them into:
    //
    //    [
    //      {
    //        fld_itemid,
    //        fld_uomid,
    //        fld_itemname,
    //        fld_uomname,
    //        qty,
    //        suppliers: [...]
    //      }
    //    ]
    // =========================================================
    const rows = Array.isArray(d.rows)
      ? d.rows
      : Array.isArray(d.gridItems)
      ? d.gridItems
      : [];

    const itemMap = new Map();

    rows.forEach((r) => {
      const quotationItemId = toInt(
        r.fld_quotationitemid ??
          r.fld_QuotationItemID,
        0
      );

      const itemId = toInt(
        r.fld_itemid ??
          r.fld_ItemId,
        0
      );

      const itemKey =
        quotationItemId > 0
          ? `QI-${quotationItemId}`
          : `ITEM-${itemId}`;

      if (!itemMap.has(itemKey)) {
        itemMap.set(itemKey, {
          fld_quotationitemid: quotationItemId,
          fld_itemid: itemId,
          fld_uomid: toInt(
            r.fld_uomid ??
              r.fld_UomId,
            0
          ),
          fld_itemname: String(
            r.fld_itemname || ""
          ),
          fld_uomname: String(
            r.fld_uomname ||
              r.fld_uomcode ||
              r.fld_uom ||
              ""
          ),
          qty: String(
            r.fld_quantity ??
              r.fld_Quantity ??
              r.qty ??
              "0"
          ),
          suppliers: [],
        });
      }

      const item = itemMap.get(itemKey);

      item.suppliers.push({
        quotationSupplierId: toInt(
          r.fld_quotatiosupplierid ??
            r.fld_QuotatioSupplierID,
          0
        ),

        supplierId: toInt(
          r.fld_supplierid ??
            r.fld_SupplierId,
          0
        ),

        supplierAddress: String(
          r.fld_suppliername || ""
        ),

        make: String(
          r.fld_make || ""
        ),

        price: r.fld_price ?? 0,
        discount: r.fld_discount ?? 0,

        vatCst: r.fld_vatcst ?? 0,
        cst: r.fld_cst ?? 0,
        ed: r.fld_ed ?? 0,

        pfStatus:
          r.qsi_pfstatus === true ||
          toInt(r.qsi_pfstatus, 0) === 1
            ? 1
            : 0,

        isApproved:
          r.fld_isapproved === true ||
          toInt(r.fld_isapproved, 0) === 1
            ? 1
            : 0,

        approvalNo: String(
          r.fld_approval_no || ""
        ),

        freight: r.fld_freight ?? 0,

        frtTax: r.fld_frttax ?? 0,
        frSGST: r.fld_frtsgst ?? 0,
        frCGST: r.fld_frtcgst ?? 0,
        frIGST: r.fld_frtigst ?? 0,

        insurance: r.fld_insurance ?? 0,

        insTax: r.fld_instax ?? 0,
        inSGST: r.fld_inssgst ?? 0,
        inCGST: r.fld_inscgst ?? 0,
        inIGST: r.fld_insigst ?? 0,

        pf: r.fld_pf ?? 0,

        pfTax: r.fld_pftax ?? 0,
        pfSGST: r.fld_pfsgst ?? 0,
        pfCGST: r.fld_pfcgst ?? 0,
        pfIGST: r.fld_pfigst ?? 0,

        tcsPercent: r.fld_tcspercent ?? 0,
        tcsAmount: r.fld_tcsamt ?? 0,

        roundoff: r.fld_roundoff ?? 0,

        location: String(
          r.fld_location || ""
        ),

        paymentTerms: String(
          r.fld_paymentterms || ""
        ),

        delivery: String(
          r.fld_delivery || ""
        ),

        validity: String(
          r.fld_validity || ""
        ),

        discountPercent:
          r.fld_discountpercent ?? 0,

        salesTax:
          r.fld_salestax ?? 0,
      });
    });

    const gridItems = Array.from(
      itemMap.values()
    );

    // =========================================================
    // 3. BUILD SUPPLIER HEADER
    // =========================================================
    const supplierMap = new Map();

    rows.forEach((r) => {
      const supplierId = toInt(
        r.fld_supplierid ??
          r.fld_SupplierId,
        0
      );

      if (!supplierId) return;

      if (!supplierMap.has(supplierId)) {
        const supplierAddress = String(
          r.fld_suppliername || ""
        );

        supplierMap.set(supplierId, {
          supplierId,

          supplierName:
            supplierAddress
              .split(".")[0]
              .trim() ||
            `Supplier ${supplierId}`,

          supplierAddress,

          make: String(
            r.fld_make || ""
          ),
        });
      }
    });

    const builtSuppliers = Array.from(
      supplierMap.values()
    );

    setSuppliers(builtSuppliers);

    // =========================================================
    // 4. BUILD ITEM GRID
    //
    // Important:
    // supplier order must match builtSuppliers order.
    // =========================================================
    const builtItems = gridItems.map(
      (gi, ri) => {
        const supplierById = new Map();

        (gi.suppliers || []).forEach((sup) => {
          supplierById.set(
            toInt(sup.supplierId, 0),
            sup
          );
        });

        const lines = builtSuppliers.map(
          (sup) => {
            const srow =
              supplierById.get(
                toInt(sup.supplierId, 0)
              ) || {};

            const ln = {
              ...blankLine(),

              price: String(
                srow.price ?? "0"
              ),

              discount: String(
                srow.discount ?? "0"
              ),

              sgst: String(
                srow.vatCst ?? "0"
              ),

              cgst: String(
                srow.cst ?? "0"
              ),

              igst: String(
                srow.ed ?? "0"
              ),

              pfStatus: toInt(
                srow.pfStatus,
                0
              ),

              approveFlag: toInt(
                srow.isApproved,
                0
              ),

              approveNo: String(
                srow.approvalNo || ""
              ),
            };

            return recalcLine(
              gi.qty,
              ln
            );
          }
        );

        return {
          rowKey: `${
            gi.fld_quotationitemid ||
            gi.fld_itemid
          }-${ri}`,

          itemId: toInt(
            gi.fld_itemid,
            0
          ),

          uomId: toInt(
            gi.fld_uomid,
            0
          ),

          itemName: String(
            gi.fld_itemname || ""
          ),

          uomName: String(
            gi.fld_uomname || ""
          ),

          qty: String(
            gi.qty ?? "0"
          ),

          lines,
        };
      }
    );

    setItems(builtItems);

    // =========================================================
    // 5. FIND APPROVED SUPPLIER
    // =========================================================
    if (
      builtItems.length > 0 &&
      builtSuppliers.length > 0
    ) {
      const idx =
        builtSuppliers.findIndex(
          (_, si) =>
            builtItems.every(
              (it) =>
                toInt(
                  it.lines?.[si]
                    ?.approveFlag,
                  0
                ) === 1
            )
        );

      setApprovedSupIdx(
        idx >= 0 ? idx : null
      );
    } else {
      setApprovedSupIdx(null);
    }

    // =========================================================
    // 6. BUILD FOOTER / CHARGES DATA
    // =========================================================
    const footerMap = {};

    builtSuppliers.forEach(
      (sup, si) => {
        const row = rows.find(
          (r) =>
            toInt(
              r.fld_supplierid ??
                r.fld_SupplierId,
              0
            ) ===
            toInt(
              sup.supplierId,
              0
            )
        );

        footerMap[si] = {
          freight: String(
            row?.fld_freight ?? "0"
          ),

          freightTaxOn:
            toInt(
              row?.fld_frttax,
              0
            ) === 1,

          freightSgst: String(
            row?.fld_frtsgst ?? "0"
          ),

          freightCgst: String(
            row?.fld_frtcgst ?? "0"
          ),

          freightIgst: String(
            row?.fld_frtigst ?? "0"
          ),

          insurance: String(
            row?.fld_insurance ?? "0"
          ),

          insuranceTaxOn:
            toInt(
              row?.fld_instax,
              0
            ) === 1,

          insuranceSgst: String(
            row?.fld_inssgst ?? "0"
          ),

          insuranceCgst: String(
            row?.fld_inscgst ?? "0"
          ),

          insuranceIgst: String(
            row?.fld_insigst ?? "0"
          ),

          pf: String(
            row?.fld_pf ?? "0"
          ),

          pfTaxOn:
            toInt(
              row?.fld_pftax,
              0
            ) === 1,

          pfSgst: String(
            row?.fld_pfsgst ?? "0"
          ),

          pfCgst: String(
            row?.fld_pfcgst ?? "0"
          ),

          pfIgst: String(
            row?.fld_pfigst ?? "0"
          ),

          tcsPercent: String(
            row?.fld_tcspercent ?? "0"
          ),

          roundoff: String(
            row?.fld_roundoff ?? "0"
          ),

          location: String(
            row?.fld_location || ""
          ),

          paymentTerms: String(
            row?.fld_paymentterms || ""
          ),

          delivery: String(
            row?.fld_delivery || ""
          ),

          validity: String(
            row?.fld_validity || ""
          ),

          discountPercent: String(
            row?.fld_discountpercent ??
              "0"
          ),

          salesTax: String(
            row?.fld_salestax ??
              "0"
          ),
        };
      }
    );

    setFooters(footerMap);
  } catch (e) {
    console.error(
      "Quotation loadView error:",
      e
    );

    toast.error(
      `Load view failed: ${
        e.response?.data?.message ||
        e.message
      }`
    );
  } finally {
    setLoading(false);
  }
}, [quotationIdParam]);
  useEffect(() => {
    loadInit();
  }, [loadInit]);
  useEffect(() => {
    if (isEditMode) loadView();
    else loadGrid();
  }, [isEditMode, loadView, loadGrid]);

  const updateQty = (rowIdx, val) =>
    setItems((prev) => {
      const next = [...prev];
      const it = { ...next[rowIdx], qty: val };
      it.lines = it.lines.map((ln) => recalcLine(val, ln));
      next[rowIdx] = it;
      return next;
    });

  const updateLine = (rowIdx, si, field, val) =>
    setItems((prev) => {
      const next = [...prev];
      const it = { ...next[rowIdx] };
      const lines = [...it.lines];
      lines[si] = recalcLine(it.qty, { ...lines[si], [field]: val });
      it.lines = lines;
      next[rowIdx] = it;
      return next;
    });

  const updateMake = (si, val) =>
    setSuppliers((prev) => {
      const next = [...prev];
      next[si] = { ...next[si], make: val };
      return next;
    });

  const updateFooter = (si, field, val) =>
    setFooters((prev) => ({
      ...prev,
      [si]: { ...(prev[si] || blankFooter()), [field]: val },
    }));

  const handleApproveRadio = (si) => {
    setApprovedSupIdx(si);
    setItems((prev) =>
      prev.map((it) => ({
        ...it,
        lines: it.lines.map((ln, li) => ({
          ...ln,
          approveFlag: li === si ? 1 : 0,
        })),
      }))
    );
  };

const buildPayload = (forApprove = false) => {
  const s = getSession();

  // PR ids from Rate Comparison URL (?pr=18,17)
  const prIds = (prParam || "")
    .split(",")
    .map((x) => toInt(x.trim(), 0))
    .filter((x) => x > 0);

  // Approved supplier (required for PO Add list)
  const approvedSupplierId =
    approvedSupIdx != null
      ? toInt(suppliers[approvedSupIdx]?.supplierId, 0)
      : 0;

  // Build requestItems for tbl_QuotationWithRequestItems
  const requestItems = prIds.flatMap((prId) =>
    items
      .map((it) => toInt(it.itemId, 0))
      .filter((id) => id > 0)
      .map((itemId) => ({
        purchaseRequestId: prId,
        itemId,
        supplierId: approvedSupplierId,
        isApproved: 1,
      }))
  );

  return {
    quotationId: quotationId || 0,
    branchId: toInt(s.branchId, 0),
    quotationNo,
    quotationDate: isoToDDMMYYYY(quotationDate),
    enquiryId: enquiryId === "Select" ? 0 : toInt(enquiryId, 0),
    employeeId: employeeId === "Select" ? "" : String(employeeId),
    remarks,
    indentNo,
    indentDate: isoToDDMMYYYY(indentDate),
    reqFor: requestedFor,
    reqSiteId: reqSiteId === "Select" ? 0 : toInt(reqSiteId, 0),

    branchType: "H",
    prIds: prIds.join(","),
    requestItems,

    ...(forApprove
      ? {
          approvedBy: s.loginUserId || "37",
          loginUserType: s.loginUserType,
          deptName: s.deptName,
        }
      : {}),

    suppliers: suppliers.map((sup, si) => {
      const f = footers[si] || blankFooter();
      return {
        supplierId: toInt(sup.supplierId, 0),
        make: sup.make || "",
        location: f.location || "",
        paymentTerms: f.paymentTerms || "",
        delivery: f.delivery || "",
        validity: f.validity || "",
        freight: toNum(f.freight, 0),
        frtTax: f.freightTaxOn ? 1 : 0,
        frSGST: toNum(f.freightSgst, 0),
        frCGST: toNum(f.freightCgst, 0),
        frIGST: toNum(f.freightIgst, 0),
        insurance: toNum(f.insurance, 0),
        insTax: f.insuranceTaxOn ? 1 : 0,
        inSGST: toNum(f.insuranceSgst, 0),
        inCGST: toNum(f.insuranceCgst, 0),
        inIGST: toNum(f.insuranceIgst, 0),
        pf: toNum(f.pf, 0),
        pfTax: f.pfTaxOn ? 1 : 0,
        pfSGST: toNum(f.pfSgst, 0),
        pfCGST: toNum(f.pfCgst, 0),
        pfIGST: toNum(f.pfIgst, 0),
        tcsPercent: toNum(f.tcsPercent, 0),
        tcsAmount: supplierTotals[si]?.tcsAmt || 0,
        roundoff: toNum(f.roundoff, 0),
        discountPercent: toNum(f.discountPercent, 0),
        salesTax: toNum(f.salesTax, 0),
      };
    }),

    items: items.map((it) => ({
      itemId: toInt(it.itemId, 0),
      uomId: toInt(it.uomId, 0),
      qty: toNum(it.qty, 0),
      lines: (it.lines || []).map((ln) => ({
        price: toNum(ln.price, 0),
        discount: toNum(ln.discount, 0),
        vatCst: toNum(ln.sgst, 0),
        cst: toNum(ln.cgst, 0),
        ed: toNum(ln.igst, 0),
        pf: 0,
        pfStatus: toInt(ln.pfStatus, 0),
        approveFlag: toInt(ln.approveFlag, 0),
        approveNo: ln.approveNo || "",
      })),
    })),
  };
};

  const validateFooterMandatoryFields = () => {
    for (let si = 0; si < suppliers.length; si++) {
      const f = footers[si] || blankFooter();
      if (!String(f.location || "").trim()) {
        toast.error(`Please enter Price Basis for ${suppliers[si]?.supplierName || `Supplier ${si + 1}`}`);
        return false;
      }
      if (!String(f.paymentTerms || "").trim()) {
        toast.error(`Please enter Terms of Payment for ${suppliers[si]?.supplierName || `Supplier ${si + 1}`}`);
        return false;
      }
      if (!String(f.delivery || "").trim()) {
        toast.error(`Please enter Delivery for ${suppliers[si]?.supplierName || `Supplier ${si + 1}`}`);
        return false;
      }
    }
    return true;
  };

  const handleSave = async () => {
    if (!quotationDate) {
      toast.error("Please enter value for Date");
      return;
    }
    if (!indentDate) {
      toast.error("Please enter value for Indent Date");
      return;
    }
    if (!requestedFor.trim()) {
      toast.error("Requested For is required");
      return;
    }
    if (suppliers.length === 0 || items.length === 0) {
      toast.error("No items loaded — cannot save");
      return;
    }
    if (approvedSupIdx === null) {
      toast.error("Please Approve Any One Supplier Items");
      return;
    }
    if (!validateFooterMandatoryFields()) {
      return;
    }
    try {
      setSaveLoading(true);
      const res = await api.post("/save", buildPayload(false));
      const data = res.data;
      if (!data.success) throw new Error(data.message);

      setUserRights((prev) => ({ ...prev, canSubmit: false, canAdd: true }));
      setQuotationId(data.quotationId);
      setQuotationNo(data.quotationNo || quotationNo);

      toast.success(data.message || "Quotation saved successfully");

      if (data.smsDetails?.text) {
        toast.info(`SMS triggered: ${data.smsDetails.text}`, { autoClose: 6000 });
      }
    } catch (e) {
      const msg = e.response?.data?.message || e.message;
      toast.error(`Save failed: ${msg}`);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleApproveSubmit = async () => {
    if (!quotationDate) {
      toast.error("Please enter value for Date");
      return;
    }
    if (approvedSupIdx === null) {
      toast.error("Please Approve Any One Supplier Items");
      return;
    }
    if (!validateFooterMandatoryFields()) {
      return;
    }
    const s = getSession();
    if (!s.loginUserId) {
      toast.error("Login user ID not found in session");
      return;
    }

    try {
      setSaveLoading(true);
      const res = await api.post("/approve", buildPayload(true));
      const data = res.data;
      if (!data.success) throw new Error(data.message);

      setUserRights((prev) => ({
        ...prev,
        canApprove: false,
        canSubmit: false,
        isLocked: true,
        mdApproved: true,
      }));
      setQuotationId(data.quotationId);

      toast.success(data.message || "Quotation approved successfully");
      if (data.smsDetails?.text) {
        toast.info(`SMS triggered: ${data.smsDetails.text}`, { autoClose: 6000 });
      }
    } catch (e) {
      toast.error(`Approve failed: ${e.response?.data?.message || e.message}`);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleUploadDoc = async () => {
    if (!docFile) {
      toast.error("Please Browse and Open the file");
      return;
    }
    if (!quotationId) {
      toast.error("Save the quotation first before uploading documents");
      return;
    }
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("doc", docFile);
      formData.append("quotationId", String(quotationId));

      const res = await api.post("/upload-doc", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const data = res.data;
      if (!data.success) throw new Error(data.message);

      setDocs((prev) => [
        ...prev,
        { fileName: data.fileName, document: data.localPath },
      ]);
      setDocFile(null);
      if (docInputRef.current) docInputRef.current.value = "";
      toast.success(`Document uploaded: ${data.fileName}`);
    } catch (e) {
      toast.error(`Upload failed: ${e.response?.data?.message || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const openSupplierModal = async (item) => {
    if (!item?.itemId) return;
    setModalItem(item);
    setShowModal(true);
    setModalRows([]);
    try {
      const res = await api.get("/item-suppliers-latest", {
        params: { itemId: item.itemId },
      });
      setModalRows(res.data?.rows || []);
    } catch {
      toast.error("Failed to load supplier details");
    }
  };

  const locked = userRights.isLocked;

  return (
    <LayoutWrapper>
      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">Quotation Entry (GST)</span>
        }
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* Header Card */}
        <div
          className={`p-6 rounded-2xl shadow-xl border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
            >
              <ClipboardList className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">
                {isEditMode ? "Edit Quotation" : "New Quotation Entry"}
              </h1>
              <p className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                GST rate comparison & approval
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => navigate("/inventory/quotations-listing")}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                isDark
                  ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                  : "border-slate-300 text-slate-700 hover:bg-slate-100"
              }`}
            >
              <ArrowLeft size={16} />
              Back
            </button>

            <button
              onClick={() => {
                loadInit();
                if (isEditMode) loadView();
                else loadGrid();
              }}
              disabled={loading}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                isDark
                  ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                  : "border-slate-300 text-slate-700 hover:bg-slate-100"
              }`}
            >
              <RefreshCw size={16} />
              Refresh
            </button>

            {userRights.canSubmit && (
              <button
                onClick={handleSave}
                disabled={saveLoading || locked}
                className={`inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass} disabled:opacity-50`}
              >
                <Save size={16} />
                {saveLoading ? "Saving..." : "Save"}
              </button>
            )}

            {userRights.canApprove && (
              <button
                onClick={handleApproveSubmit}
                disabled={saveLoading || locked}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-xl shadow transition disabled:opacity-50"
              >
                <CheckCircle size={16} />
                {saveLoading ? "Approving..." : "Approve"}
              </button>
            )}
          </div>
        </div>

        {locked && (
          <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-400 dark:border-yellow-700 rounded-xl text-yellow-800 dark:text-yellow-200 text-sm font-semibold">
            This quotation has been approved by MD. All fields are read-only.
          </div>
        )}

        {/* Header Form Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
              <h3 className="text-xl font-bold">Quotation Header</h3>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
              {/* Left */}
              <div className="space-y-5">
                <div>
                  <label className={labelClass}>Quotation No</label>
                  <div
                    className={`px-3 py-2.5 rounded-lg border text-sm min-h-[42px] ${
                      isDark
                        ? "bg-slate-800/40 border-slate-600 text-white"
                        : "bg-gray-50 border-slate-200"
                    }`}
                  >
                    {quotationNo || (
                      <span className="text-gray-400">Auto-generated</span>
                    )}
                  </div>
                </div>

                <div>
                  <label className={labelClass}>
                    Quotation Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={quotationDate}
                    max={today()}
                    onChange={(e) => setQuotationDate(e.target.value)}
                    disabled={locked}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Enquiry No</label>
                  <select
                    value={enquiryId}
                    onChange={(e) => setEnquiryId(e.target.value)}
                    disabled={locked}
                    className={inputClass}
                  >
                    <option value="Select">Select</option>
                    {enquiryList.map((x, i) => {
                      const val = x.fld_enquiryid ?? x.value ?? i;
                      const lbl = x.fld_enquiryno ?? x.text ?? val;
                      return (
                        <option key={val} value={val}>
                          {lbl}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Indent No</label>
                  <input
                    type="text"
                    value={indentNo}
                    onChange={(e) => setIndentNo(e.target.value)}
                    disabled={locked}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Requested For <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={requestedFor}
                    onChange={(e) => setRequestedFor(e.target.value)}
                    disabled={locked}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Project Name</label>
                  <div
                    className={`px-3 py-2.5 rounded-lg border text-sm min-h-[42px] ${
                      isDark
                        ? "bg-slate-800/40 border-slate-600 text-white"
                        : "bg-gray-50 border-slate-200"
                    }`}
                  >
                    {projectName || <span className="text-gray-400">—</span>}
                  </div>
                </div>
              </div>

              {/* Right */}
              <div className="space-y-5">
                <div>
                  <label className={labelClass}>Requested Site</label>
                  <select
                    value={reqSiteId}
                    onChange={(e) => setReqSiteId(e.target.value)}
                    disabled={locked}
                    className={inputClass}
                  >
                    <option value="Select">Select</option>
                    {reqSiteList.map((x, i) => {
                      const val = x.fld_Branchid ?? x.value ?? i;
                      const lbl = x.fld_BranchName ?? x.text ?? val;
                      return (
                        <option key={val} value={val}>
                          {lbl}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>
                    Indent Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={indentDate}
                    max={today()}
                    onChange={(e) => setIndentDate(e.target.value)}
                    disabled={locked}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Employee (Signature)</label>
                  <select
                    value={employeeId}
                    onChange={(e) => setEmployeeId(e.target.value)}
                    disabled={locked}
                    className={inputClass}
                  >
                    <option value="Select">Select</option>
                    {employeeList.map((x, i) => {
                      const val = x.fld_employeeid ?? x.value ?? i;
                      const lbl = x.fld_employee ?? x.text ?? val;
                      return (
                        <option key={val} value={val}>
                          {lbl}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Remarks</label>
                  <textarea
                    value={remarks}
                    rows={3}
                    onChange={(e) => setRemarks(e.target.value)}
                    disabled={locked}
                    className={`${inputClass} resize-none`}
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Attach Document
                    {!userRights.canAdd && (
                      <span className="ml-2 text-xs text-gray-400 font-normal">
                        (save quotation first)
                      </span>
                    )}
                  </label>
                  <div className="flex gap-2">
                    <input
                      ref={docInputRef}
                      type="file"
                      onChange={(e) => setDocFile(e.target.files?.[0] || null)}
                      disabled={locked || !userRights.canAdd}
                      className={`flex-1 px-3 py-2 rounded-lg border text-sm ${
                        isDark
                          ? "bg-slate-800/60 border-slate-600 text-white"
                          : "bg-white border-slate-300"
                      }`}
                    />
                    <button
                      onClick={handleUploadDoc}
                      disabled={locked || !userRights.canAdd || !docFile || loading}
                      className={`inline-flex items-center gap-1 px-3 py-2 text-white text-sm font-semibold rounded-lg shadow transition ${buttonClass} ${hoverClass} disabled:opacity-50`}
                    >
                      <Upload size={14} />
                      Add
                    </button>
                  </div>
                  {docs.length > 0 && (
                    <div
                      className={`mt-2 rounded-lg p-2 max-h-24 overflow-y-auto space-y-1 ${
                        isDark ? "bg-slate-800/40" : "bg-gray-50"
                      }`}
                    >
                      {docs.map((d, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          <FileText size={14} className="text-red-500 flex-shrink-0" />
                          <span className="truncate">{d.fileName || d.document}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 text-sm text-gray-500 pt-1">
                  <Info size={14} />
                  <span>
                    Click <b>Item Name</b> to view Supplier Details
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {loading && (
          <div className="text-center py-12 text-gray-500 text-sm">Loading…</div>
        )}

        {!loading && suppliers.length === 0 && (
          <div
            className={`p-6 rounded-2xl border text-center text-gray-500 text-sm ${
              isDark
                ? "bg-slate-800/40 border-slate-700"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            <Info size={18} className="inline mr-2" />
            {isEditMode
              ? "No supplier data found for this quotation."
              : "Waiting for supplier/item data. Navigate here from Rate Comparison page (?pr=&su=&itm=)"}
          </div>
        )}

        {/* Rate Comparison Grid */}
        {!loading && suppliers.length > 0 && (
          <div
            className={`rounded-2xl shadow-xl border overflow-hidden ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                : `bg-white ${currentSkin.glass} border-gray-200`
            }`}
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
                <h3 className="text-xl font-bold">Rate Comparison Grid</h3>
              </div>
            </div>

            <div className="overflow-x-auto p-4">
              <table className="min-w-max w-full border-collapse text-xs">
                <thead>
                  <tr>
                    <th
                      className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white border px-3 py-2 w-10`}
                      rowSpan={2}
                    >
                      S.No
                    </th>
                    <th
                      className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white border px-3 py-2 min-w-[180px]`}
                      rowSpan={2}
                    >
                      Description of Item
                    </th>
                    <th
                      className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white border px-3 py-2 w-16`}
                      rowSpan={2}
                    >
                      Unit
                    </th>
                    <th
                      className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white border px-3 py-2 w-20`}
                      rowSpan={2}
                    >
                      Qty
                    </th>

                    {suppliers.map((sup, si) => (
                      <th
                        key={si}
                        className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white border px-3 py-2 min-w-[560px]`}
                      >
                        <div className="font-semibold text-left">{sup.supplierName}</div>
                        <div className="text-xs opacity-80 text-left truncate max-w-[540px]">
                          {sup.supplierAddress}
                        </div>

                        <div className="flex items-center gap-2 mt-2">
                          <span className="font-semibold whitespace-nowrap">Make</span>
                          <input
                            value={sup.make || ""}
                            onChange={(e) => updateMake(si, e.target.value)}
                            disabled={locked}
                            className="border rounded px-2 py-1 text-black text-xs w-44"
                          />
                          <label className="ml-auto flex items-center gap-1 font-semibold whitespace-nowrap">
                            <input
                              type="radio"
                              name="approvedSup"
                              checked={approvedSupIdx === si}
                              onChange={() => !locked && handleApproveRadio(si)}
                              disabled={locked}
                            />
                            Appr.
                          </label>
                        </div>

                        <div className="grid grid-cols-7 gap-1 mt-2 font-bold text-left text-xs">
                          <span>Price</span>
                          <span>Dis%</span>
                          <span>Taxable</span>
                          <span>SGST%</span>
                          <span>CGST%</span>
                          <span>IGST%</span>
                          <span>Total</span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td
                        colSpan={4 + suppliers.length}
                        className="text-center py-8 text-gray-400 border"
                      >
                        No items found
                      </td>
                    </tr>
                  ) : (
                    items.map((it, rowIdx) => (
                      <tr
                        key={it.rowKey}
                        className={`text-center ${
                          isDark ? "hover:bg-slate-800/30" : "hover:bg-gray-50"
                        }`}
                      >
                        <td className="border px-2 py-2">{rowIdx + 1}</td>
                        <td className="border px-2 py-2 text-left">
                          <button
                            type="button"
                            onClick={() => openSupplierModal(it)}
                            className={`text-${baseColor}-600 underline hover:opacity-80 text-left text-xs font-medium`}
                          >
                            {it.itemName}
                          </button>
                        </td>
                        <td className="border px-2 py-2 text-xs">{it.uomName}</td>
                        <td className="border px-2 py-2">
                          <input
                            className={`${numInputClass} w-20`}
                            value={String(it.qty)}
                            onChange={(e) => updateQty(rowIdx, e.target.value)}
                            disabled={locked}
                          />
                        </td>

                        {suppliers.map((_, si) => {
                          const ln = it.lines?.[si] || blankLine();
                          return (
                            <td key={si} className="border px-2 py-2">
                              <div className="grid grid-cols-7 gap-1 items-center">
                                <input
                                  className={`${numInputClass} w-24`}
                                  value={String(ln.price ?? "0")}
                                  onChange={(e) =>
                                    updateLine(rowIdx, si, "price", e.target.value)
                                  }
                                  disabled={locked}
                                />
                                <input
                                  className={`${numInputClass} w-16`}
                                  value={String(ln.discount ?? "0")}
                                  onChange={(e) =>
                                    updateLine(rowIdx, si, "discount", e.target.value)
                                  }
                                  disabled={locked}
                                />
                                <div className="text-right font-semibold px-1 text-xs">
                                  {fmt2(ln.taxable ?? 0)}
                                </div>
                                <TaxSelect
                                  options={tax1}
                                  value={ln.sgst ?? "0"}
                                  onChange={(v) => updateLine(rowIdx, si, "sgst", v)}
                                  disabled={locked}
                                  className="w-16"
                                  isDark={isDark}
                                />
                                <TaxSelect
                                  options={tax2}
                                  value={ln.cgst ?? "0"}
                                  onChange={(v) => updateLine(rowIdx, si, "cgst", v)}
                                  disabled={locked}
                                  className="w-16"
                                  isDark={isDark}
                                />
                                <TaxSelect
                                  options={tax3}
                                  value={ln.igst ?? "0"}
                                  onChange={(v) => updateLine(rowIdx, si, "igst", v)}
                                  disabled={locked}
                                  className="w-16"
                                  isDark={isDark}
                                />
                                <div className="text-right font-bold px-1 text-xs">
                                  {fmt2(ln.total ?? 0)}
                                </div>
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Footer Charges / Terms */}
        {!loading && suppliers.length > 0 && (
          <div
            className={`rounded-2xl shadow-xl border overflow-hidden ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                : `bg-white ${currentSkin.glass} border-gray-200`
            }`}
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
                <h3 className="text-xl font-bold">Charges / Terms</h3>
              </div>
            </div>

            <div className="overflow-x-auto p-4">
              <table className="min-w-max w-full border-collapse text-xs">
                <thead>
                  <tr>
                    <th
                      className={`border px-3 py-2 text-left w-52 font-semibold text-sm ${
                        isDark ? "bg-slate-800" : "bg-gray-100"
                      }`}
                    >
                      Charges / Terms
                    </th>
                    {suppliers.map((s, si) => (
                      <th
                        key={si}
                        className={`border px-3 py-2 text-left min-w-[420px] font-semibold ${
                          isDark ? "bg-slate-800" : "bg-gray-100"
                        }`}
                      >
                        {s.supplierName}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border px-3 py-2 font-semibold">Sub Total</td>
                    {suppliers.map((_, si) => (
                      <td key={si} className="border px-3 py-2 text-right font-bold">
                        {fmt2(supplierTotals[si]?.subTotal ?? 0)}
                      </td>
                    ))}
                  </tr>

                  <tr>
                    <td className="border px-3 py-2 font-semibold">
                      Freight{" "}
                      <span className="text-xs text-gray-500">(tax + SGST/CGST/IGST)</span>
                    </td>
                    {suppliers.map((_, si) => {
                      const f = footers[si] || blankFooter();
                      return (
                        <td key={si} className="border px-3 py-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <input
                              className={`${numInputClass} w-24`}
                              value={f.freight}
                              onChange={(e) =>
                                updateFooter(si, "freight", e.target.value)
                              }
                              disabled={locked}
                            />
                            <label className="flex items-center gap-1">
                              <input
                                type="checkbox"
                                checked={!!f.freightTaxOn}
                                onChange={(e) =>
                                  updateFooter(si, "freightTaxOn", e.target.checked)
                                }
                                disabled={locked}
                              />{" "}
                              Tax
                            </label>
                            <TaxSelect
                              options={tax1}
                              value={f.freightSgst}
                              onChange={(v) => updateFooter(si, "freightSgst", v)}
                              disabled={locked}
                              className="w-14"
                              isDark={isDark}
                            />
                            <TaxSelect
                              options={tax2}
                              value={f.freightCgst}
                              onChange={(v) => updateFooter(si, "freightCgst", v)}
                              disabled={locked}
                              className="w-14"
                              isDark={isDark}
                            />
                            <TaxSelect
                              options={tax3}
                              value={f.freightIgst}
                              onChange={(v) => updateFooter(si, "freightIgst", v)}
                              disabled={locked}
                              className="w-14"
                              isDark={isDark}
                            />
                            <span className="ml-auto font-semibold text-blue-600">
                              Tax: {fmt2(supplierTotals[si]?.frTax ?? 0)}
                            </span>
                          </div>
                        </td>
                      );
                    })}
                  </tr>

                  <tr>
                    <td className="border px-3 py-2 font-semibold">Insurance</td>
                    {suppliers.map((_, si) => {
                      const f = footers[si] || blankFooter();
                      return (
                        <td key={si} className="border px-3 py-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <input
                              className={`${numInputClass} w-24`}
                              value={f.insurance}
                              onChange={(e) =>
                                updateFooter(si, "insurance", e.target.value)
                              }
                              disabled={locked}
                            />
                            <label className="flex items-center gap-1">
                              <input
                                type="checkbox"
                                checked={!!f.insuranceTaxOn}
                                onChange={(e) =>
                                  updateFooter(si, "insuranceTaxOn", e.target.checked)
                                }
                                disabled={locked}
                              />{" "}
                              Tax
                            </label>
                            <TaxSelect
                              options={tax1}
                              value={f.insuranceSgst}
                              onChange={(v) => updateFooter(si, "insuranceSgst", v)}
                              disabled={locked}
                              className="w-14"
                              isDark={isDark}
                            />
                            <TaxSelect
                              options={tax2}
                              value={f.insuranceCgst}
                              onChange={(v) => updateFooter(si, "insuranceCgst", v)}
                              disabled={locked}
                              className="w-14"
                              isDark={isDark}
                            />
                            <TaxSelect
                              options={tax3}
                              value={f.insuranceIgst}
                              onChange={(v) => updateFooter(si, "insuranceIgst", v)}
                              disabled={locked}
                              className="w-14"
                              isDark={isDark}
                            />
                            <span className="ml-auto font-semibold text-blue-600">
                              Tax: {fmt2(supplierTotals[si]?.inTax ?? 0)}
                            </span>
                          </div>
                        </td>
                      );
                    })}
                  </tr>

                  <tr>
                    <td className="border px-3 py-2 font-semibold">P &amp; F Charges</td>
                    {suppliers.map((_, si) => {
                      const f = footers[si] || blankFooter();
                      return (
                        <td key={si} className="border px-3 py-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <input
                              className={`${numInputClass} w-24`}
                              value={f.pf}
                              onChange={(e) => updateFooter(si, "pf", e.target.value)}
                              disabled={locked}
                            />
                            <label className="flex items-center gap-1">
                              <input
                                type="checkbox"
                                checked={!!f.pfTaxOn}
                                onChange={(e) =>
                                  updateFooter(si, "pfTaxOn", e.target.checked)
                                }
                                disabled={locked}
                              />{" "}
                              Tax
                            </label>
                            <TaxSelect
                              options={tax1}
                              value={f.pfSgst}
                              onChange={(v) => updateFooter(si, "pfSgst", v)}
                              disabled={locked}
                              className="w-14"
                              isDark={isDark}
                            />
                            <TaxSelect
                              options={tax2}
                              value={f.pfCgst}
                              onChange={(v) => updateFooter(si, "pfCgst", v)}
                              disabled={locked}
                              className="w-14"
                              isDark={isDark}
                            />
                            <TaxSelect
                              options={tax3}
                              value={f.pfIgst}
                              onChange={(v) => updateFooter(si, "pfIgst", v)}
                              disabled={locked}
                              className="w-14"
                              isDark={isDark}
                            />
                            <span className="ml-auto font-semibold text-blue-600">
                              Tax: {fmt2(supplierTotals[si]?.pfTax ?? 0)}
                            </span>
                          </div>
                        </td>
                      );
                    })}
                  </tr>

                  <tr>
                    <td className="border px-3 py-2 font-semibold">TCS (%)</td>
                    {suppliers.map((_, si) => {
                      const f = footers[si] || blankFooter();
                      return (
                        <td key={si} className="border px-3 py-2">
                          <div className="flex items-center gap-3">
                            <input
                              className={`${numInputClass} w-24`}
                              value={f.tcsPercent}
                              onChange={(e) =>
                                updateFooter(si, "tcsPercent", e.target.value)
                              }
                              disabled={locked}
                            />
                            <span className="ml-auto font-semibold text-blue-600">
                              Amt: {fmt2(supplierTotals[si]?.tcsAmt ?? 0)}
                            </span>
                          </div>
                        </td>
                      );
                    })}
                  </tr>

                  <tr>
                    <td className="border px-3 py-2 font-semibold">Round off</td>
                    {suppliers.map((_, si) => {
                      const f = footers[si] || blankFooter();
                      return (
                        <td key={si} className="border px-3 py-2">
                          <input
                            className={`${numInputClass} w-24`}
                            value={f.roundoff}
                            onChange={(e) =>
                              updateFooter(si, "roundoff", e.target.value)
                            }
                            disabled={locked}
                          />
                        </td>
                      );
                    })}
                  </tr>

                  <tr className={isDark ? "bg-slate-800/40" : "bg-gray-50"}>
                    <td className="border px-3 py-2 font-bold text-sm">Grand Total</td>
                    {suppliers.map((_, si) => (
                      <td
                        key={si}
                        className="border px-3 py-2 text-right font-extrabold text-base"
                      >
                        {fmt2(supplierTotals[si]?.grandTotal ?? 0)}
                      </td>
                    ))}
                  </tr>

                  <tr>
                    <td className="border px-3 py-2 font-semibold">
                      Price Basis <span className="text-red-500">*</span>
                    </td>
                    {suppliers.map((_, si) => {
                      const f = footers[si] || blankFooter();
                      return (
                        <td key={si} className="border px-3 py-2">
                          <input
                            className={inputClass}
                            value={f.location}
                            onChange={(e) =>
                              updateFooter(si, "location", e.target.value)
                            }
                            placeholder="Ex: FOR Site / Ex-Works"
                            disabled={locked}
                          />
                        </td>
                      );
                    })}
                  </tr>

                  <tr>
                    <td className="border px-3 py-2 font-semibold">
                      Terms of Payment <span className="text-red-500">*</span>
                    </td>
                    {suppliers.map((_, si) => {
                      const f = footers[si] || blankFooter();
                      return (
                        <td key={si} className="border px-3 py-2">
                          <input
                            className={inputClass}
                            value={f.paymentTerms}
                            onChange={(e) =>
                              updateFooter(si, "paymentTerms", e.target.value)
                            }
                            placeholder="Ex: 30 days credit"
                            disabled={locked}
                          />
                        </td>
                      );
                    })}
                  </tr>

                  <tr>
                    <td className="border px-3 py-2 font-semibold">
                      Delivery <span className="text-red-500">*</span>
                    </td>
                    {suppliers.map((_, si) => {
                      const f = footers[si] || blankFooter();
                      return (
                        <td key={si} className="border px-3 py-2">
                          <input
                            className={inputClass}
                            value={f.delivery}
                            onChange={(e) =>
                              updateFooter(si, "delivery", e.target.value)
                            }
                            placeholder="Ex: Within 7 days"
                            disabled={locked}
                          />
                        </td>
                      );
                    })}
                  </tr>

                  <tr>
                    <td className="border px-3 py-2 font-semibold">Validity (in Days)</td>
                    {suppliers.map((_, si) => {
                      const f = footers[si] || blankFooter();
                      return (
                        <td key={si} className="border px-3 py-2">
                          <input
                            className={`${numInputClass} w-40`}
                            value={f.validity}
                            onChange={(e) =>
                              updateFooter(si, "validity", e.target.value)
                            }
                            placeholder="Ex: 30"
                            disabled={locked}
                          />
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Supplier Details Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div
            className={`w-full max-w-5xl rounded-2xl shadow-2xl border overflow-hidden ${
              isDark ? "bg-slate-900 border-slate-700" : "bg-white"
            }`}
          >
            <div
              className={`flex items-center justify-between px-5 py-4 bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
            >
              <div>
                <div className="text-base font-bold">Supplier Details</div>
                <div className="text-sm opacity-90">{modalItem?.itemName}</div>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="p-1.5 rounded-full hover:bg-white/20 transition"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-4 overflow-auto max-h-[70vh]">
              <table className="w-full text-sm">
                <thead
                  className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                >
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-bold uppercase">S.No</th>
                    <th className="px-3 py-2 text-left text-xs font-bold uppercase">
                      Supplier Name
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-bold uppercase">Price</th>
                    <th className="px-3 py-2 text-left text-xs font-bold uppercase">Date</th>
                    <th className="px-3 py-2 text-left text-xs font-bold uppercase">Branch</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? "divide-slate-700" : "divide-gray-200"}`}>
                  {modalRows.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-8 text-gray-400">
                        No records found
                      </td>
                    </tr>
                  ) : (
                    modalRows.map((r, i) => (
                      <tr
                        key={i}
                        className={
                          isDark ? "hover:bg-slate-800/40" : "hover:bg-gray-50"
                        }
                      >
                        <td className="px-3 py-2 text-center">{i + 1}</td>
                        <td className="px-3 py-2 text-left">
                          {r.fld_suppliername ?? r.supplierName ?? "—"}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {r.fld_price ?? r.price ?? "—"}
                        </td>
                        <td className="px-3 py-2">{r.fld_date ?? r.date ?? "—"}</td>
                        <td className="px-3 py-2">
                          {r.fld_BranchName ?? r.branchName ?? "—"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />
    </LayoutWrapper>
  );
}